import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { SyncDisabled, Warning } from '@mui/icons-material';
import styled from 'styled-components';
import COLOR from 'utils/constants/color';

const legendKeys = {
    late: "late",
    diff: "diff",
    tbg: 'tbg',
    game: '1game',
    story: 'story',
    onedebate: '1Debate'
}

const getCellStyle = (key) => {
    if (key === legendKeys.late) {
        return ({ backgroundColor: "rgba(255,0,0,0.8)" })
    } else if (key === legendKeys.diff) {
        return ({ backgroundColor: "#00ff134f" })
    } else if (key === legendKeys.tbg) {
        return ({ backgroundColor: "#72bef1e0" })
    } else if (key === legendKeys.game) {
        return ({ backgroundColor: COLOR.sky200 })
    } else if (key === legendKeys.story) {
        return ({ backgroundColor: COLOR.indigo600 })
    }
    else if (key === legendKeys.onedebate) {
        return ({ backgroundColor: "rgb(0, 45, 117)" })
    }
    else {
        return ({})
    }
}

const getBadgeColors = (key) => {
    if (key === legendKeys.late) {
        return { bg: "#fee1e1", border: "#ef0b0b" }
    } else if (key === legendKeys.diff) {
        return { bg: "#d1fae5", border: "#00ff13" }
    } else if (key === legendKeys.tbg) {
        return { bg: "#dbeafe", border: "#72bef1" }
    } else if (key === legendKeys.game) {
        return { bg: "#e0f2fe", border: COLOR.sky200 }
    } else if (key === legendKeys.story) {
        return { bg: "#e9d5ff", border: COLOR.indigo600 }
    }
    else if (key === legendKeys.onedebate) {
        return { bg: "#dbeafe", border: "rgb(0, 45, 117)" }
    }
    else {
        return { bg: "#f3f4f6", border: COLOR.gray100 }
    }
}

const TbgContainer = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${COLOR.white};
    border-radius: 8px;
    padding: 24px;
    max-width: 500px;
    width: 100%;
    height: 90vh;
    box-shadow: 0px 4px 12px rgba(0,0,0,0.15);
    position: relative;
    overflow: hidden;
`

const TbgHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    position: relative;
`

const TbgTitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: center;
    text-align: center;
`

const TbgTitle = styled.h2`
    color: ${COLOR.black200};
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    margin-bottom: 4px;
    line-height: 1.4;
    text-align: center;
`

const TbgSubtitle = styled.p`
    color: #686b71;
    font-size: 14px;
    font-weight: 400;
    margin: 0;
    line-height: 1.4;
    text-align: center;
`

const TbgCloseButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${COLOR.black800};
    font-size: 18px;
    line-height: 1;
    width: 24px;
    height: 24px;
    &:hover {
        opacity: 0.7;
    }
`

const TbgList = styled.ul`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 0;
    padding: 0;
    list-style: none;
    gap: 12px;
    overflow-y: auto;
    flex: 1;
    padding-right: 8px;
    
    &::-webkit-scrollbar {
        width: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
    }
`

const TbgListItem = styled.li`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    background-color: ${COLOR.white};
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.2s ease;
    
    &:hover {
        box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.15);
    }
`

const TbgItemContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 8px;
`

const TbgItemNameWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    background-color: ${props => props.$bgColor || "#f3f4f6"};
    border: 1px solid ${props => props.$borderColor || "#e5e7eb"};
    border-radius: 20px;
    padding: 4px 10px;
    width: fit-content;
`

const TbgDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 6px;
    flex-shrink: 0;
`

const TbgIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 6px;
    flex-shrink: 0;
    color: ${COLOR.gray100};
`

const TbgItemName = styled.span`
    color: ${COLOR.black200};
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
`

const TbgItemDescription = styled.span`
    color: #686b71;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.5;
`

const TbgLabels = ({ onClose }) => {
    return (
        <TbgContainer>
            <TbgHeader>
                <TbgTitleWrapper>
                    <TbgTitle>Legends</TbgTitle>
                </TbgTitleWrapper>
                <TbgCloseButton onClick={onClose}>
                    <CloseOutlined style={{ color: '#000' }} />
                </TbgCloseButton>
            </TbgHeader>
            <TbgList>
                <TbgListItem>
                    <TbgItemContent>
                        <TbgItemNameWrapper $bgColor={getBadgeColors(legendKeys.tbg).bg} $borderColor={getBadgeColors(legendKeys.tbg).border}>
                            <TbgDot style={getCellStyle(legendKeys.tbg)} />
                            <TbgItemName>TBG - AI</TbgItemName>
                        </TbgItemNameWrapper>
                        <TbgItemDescription>To Be Graded With AI</TbgItemDescription>
                    </TbgItemContent>
                </TbgListItem>
                <TbgListItem>
                    <TbgItemContent>
                        <TbgItemNameWrapper $bgColor={getBadgeColors(null).bg} $borderColor={getBadgeColors(null).border}>
                            <TbgIcon>
                                <SyncDisabled style={{ fontSize: '14px' }} />
                            </TbgIcon>
                            <TbgItemName>No Integration Sync</TbgItemName>
                        </TbgItemNameWrapper>
                        <TbgItemDescription>Assignment not synced with external platform</TbgItemDescription>
                    </TbgItemContent>
                </TbgListItem>
                <TbgListItem>
                    <TbgItemContent>
                        <TbgItemNameWrapper $bgColor="#fef3c7" $borderColor="#fbbf24">
                            <TbgIcon>
                                <Warning style={{ color: COLOR.yellow, fontSize: 14 }} />
                            </TbgIcon>
                            <TbgItemName>Assignment No Longer Available</TbgItemName>
                        </TbgItemNameWrapper>
                        <TbgItemDescription>This assignment is no longer available in integration</TbgItemDescription>
                    </TbgItemContent>
                </TbgListItem>
                <TbgListItem>
                    <TbgItemContent>
                        <TbgItemNameWrapper $bgColor={getBadgeColors(legendKeys.late).bg} $borderColor={getBadgeColors(legendKeys.late).border}>
                            <TbgDot style={getCellStyle(legendKeys.late)} />
                            <TbgItemName>Late Submission</TbgItemName>
                        </TbgItemNameWrapper>
                        <TbgItemDescription>Assignment submitted after the due date</TbgItemDescription>
                    </TbgItemContent>
                </TbgListItem>
                <TbgListItem>
                    <TbgItemContent>
                        <TbgItemNameWrapper $bgColor={getBadgeColors(legendKeys.diff).bg} $borderColor={getBadgeColors(legendKeys.diff).border}>
                            <TbgDot style={getCellStyle(legendKeys.diff)} />
                            <TbgItemName>Differentiation</TbgItemName>
                        </TbgItemNameWrapper>
                        <TbgItemDescription>Differentiation configuration applied for student.</TbgItemDescription>
                    </TbgItemContent>
                </TbgListItem>
                <TbgListItem>
                    <TbgItemContent>
                        <TbgItemNameWrapper $bgColor={getBadgeColors(legendKeys.game).bg} $borderColor={getBadgeColors(legendKeys.game).border}>
                            <TbgDot style={getCellStyle(legendKeys.game)} />
                            <TbgItemName>1GAME</TbgItemName>
                        </TbgItemNameWrapper>
                        <TbgItemDescription>Game-based assignment</TbgItemDescription>
                    </TbgItemContent>
                </TbgListItem>
                <TbgListItem>
                    <TbgItemContent>
                        <TbgItemNameWrapper $bgColor={getBadgeColors(legendKeys.onedebate).bg} $borderColor={getBadgeColors(legendKeys.onedebate).border}>
                            <TbgDot style={getCellStyle(legendKeys.onedebate)} />
                            <TbgItemName>1Debate, Lotería</TbgItemName>
                        </TbgItemNameWrapper>
                        <TbgItemDescription>Debate or Lotería activity</TbgItemDescription>
                    </TbgItemContent>
                </TbgListItem>
                <TbgListItem>
                    <TbgItemContent>
                        <TbgItemNameWrapper $bgColor={getBadgeColors(legendKeys.story).bg} $borderColor={getBadgeColors(legendKeys.story).border}>
                            <TbgDot style={getCellStyle(legendKeys.story)} />
                            <TbgItemName>1WayStory</TbgItemName>
                        </TbgItemNameWrapper>
                        <TbgItemDescription>Story-based assignment</TbgItemDescription>
                    </TbgItemContent>
                </TbgListItem>

                <TbgListItem>
                    <TbgItemContent>
                        <TbgItemNameWrapper $bgColor="#e0e7ff" $borderColor={COLOR.indigo400}>
                            <TbgDot style={{ backgroundColor: COLOR.indigo400 }} />
                            <TbgItemName>Grammar Drill</TbgItemName>
                        </TbgItemNameWrapper>
                        <TbgItemDescription>Assignment belongs to grammar drill</TbgItemDescription>
                    </TbgItemContent>
                </TbgListItem>
                <TbgListItem>
                    <TbgItemContent>
                        <TbgItemNameWrapper $bgColor="#fee1e1" $borderColor={COLOR.red}>
                            <TbgDot style={{ backgroundColor: COLOR.red }} />
                            <TbgItemName>Inactive Assignment</TbgItemName>
                        </TbgItemNameWrapper>
                        <TbgItemDescription>Assignment is deactivated</TbgItemDescription>
                    </TbgItemContent>
                </TbgListItem>
            </TbgList>
        </TbgContainer>
    )
}

export default TbgLabels;
